import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, TrendingUp, LineChart, Calendar, CalendarRange, DollarSign, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { FlatRateTemplate, getFlatRateTemplatesByService, deleteFlatRateTemplate } from '../lib/flatRateTemplates';
import { EstimatedProfitReport } from '../components/EstimatedProfitReport';

const reportPages = [
  { id: 'sales', name: 'Sales', icon: TrendingUp, path: '/sales-reports/sales' },
  { id: 'profit-loss', name: 'Profit & Loss', icon: LineChart, path: '/sales-reports/profit-loss' },
  { id: 'cash-flow', name: 'Cash Flow', icon: Wallet, path: '/sales-reports/cash-flow' }
];

const dateRanges = [
  { id: 'this-month', name: 'This Month' },
  { id: 'last-month', name: 'Last Month' },
  { id: 'next-month', name: 'Next Month' },
  { id: 'custom', name: 'Custom Range', icon: CalendarRange }
];

// Mock data for cash flow overview
const mockCashFlowData = {
  cashInflow: {
    total: 45678.90,
    breakdown: [
      { category: 'Service Revenue', amount: 35000.00 },
      { category: 'Product Sales', amount: 8500.00 },
      { category: 'Other Income', amount: 2178.90 }
    ]
  },
  cashOutflow: {
    total: 32456.78,
    breakdown: [
      { category: 'Operating Expenses', amount: 15000.00 },
      { category: 'Payroll', amount: 12000.00 },
      { category: 'Inventory', amount: 3456.78 },
      { category: 'Other Expenses', amount: 2000.00 }
    ]
  },
  netCashFlow: 13222.12
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const getDateRangeText = (rangeId: string, startDate?: string, endDate?: string): string => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  switch (rangeId) {
    case 'this-month':
      return `${formatDate(firstDayOfMonth)} - ${formatDate(lastDayOfMonth)}`;
    case 'last-month':
      const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return `${formatDate(firstDayLastMonth)} - ${formatDate(lastDayLastMonth)}`;
    case 'next-month':
      const firstDayNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const lastDayNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      return `${formatDate(firstDayNextMonth)} - ${formatDate(lastDayNextMonth)}`;
    case 'custom':
      if (startDate && endDate) {
        return `${formatDate(new Date(startDate))} - ${formatDate(new Date(endDate))}`;
      }
      return 'Custom Range';
    default:
      return '';
  }
};

// Helper function to get days in month
const getDaysInMonth = (date: Date): Date[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
};

// Helper function to format day
const formatDay = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    day: 'numeric',
    timeZone: 'UTC'
  });
};

// Helper function to get day name
const getDayName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    timeZone: 'UTC'
  });
};

interface FlatRateItem {
  hours: number;
}

interface BookingFlatRate {
  hours: number;
  item?: FlatRateItem;
}

export default function CashFlow() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(dateRanges[0]);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness } = useBusiness();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [daysInMonth, setDaysInMonth] = useState<Date[]>([]);
  const [totalSales, setTotalSales] = useState(0);
  const [completedSales, setCompletedSales] = useState(0);
  const [pendingSales, setPendingSales] = useState(0);
  const [totalFlatRateEarnings, setTotalFlatRateEarnings] = useState(0);
  const [estimatedFlatRateEarnings, setEstimatedFlatRateEarnings] = useState(0);
  const [totalHourlyEarnings, setTotalHourlyEarnings] = useState(0);
  const [totalSuppliesCost, setTotalSuppliesCost] = useState(0);
  const [totalFixedExpenses, setTotalFixedExpenses] = useState(0);
  const [estimatedHourlyEarnings, setEstimatedHourlyEarnings] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCurrentPageName = () => {
    const currentPage = reportPages.find(page => page.path === location.pathname) || reportPages[0];
    return currentPage.name;
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setIsDateDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Update days when selected date changes
    setDaysInMonth(getDaysInMonth(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setIsLoading(true);
        if (!supabase) {
          console.error('Supabase client not initialized');
          return;
        }

        let startDate, endDate;
        const now = new Date();
        
        // Set date range based on selection
        switch (selectedDateRange.id) {
          case 'this-month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            break;
          case 'last-month':
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0);
            break;
          case 'next-month':
            startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              startDate = new Date(customStartDate);
              endDate = new Date(customEndDate);
            }
            break;
        }

        if (startDate && endDate) {
          try {
            // Fetch bookings for the selected date range
            const { data: bookings, error: bookingsError } = await supabase
              .from('bookings')
              .select(`
                *,
                discounts:booking_discounts(*)
              `)
              .eq('business_id', selectedBusiness?.id)
              .gte('end_date', startDate.toISOString().split('T')[0])
              .lte('end_date', endDate.toISOString().split('T')[0]);

            if (bookingsError) {
              console.error('Error fetching bookings:', bookingsError);
              return;
            }

            // Calculate total sales (subtotal - discounts)
            const completedTotal = bookings?.reduce((sum, booking) => {
              if (booking.status === 'completed') {
                const subtotal = booking.subtotal || 0;
                const discountAmount = booking.discount_amount || 0;
                return sum + (subtotal - discountAmount);
              }
              return sum;
            }, 0) || 0;

            const pendingTotal = bookings?.reduce((sum, booking) => {
              if (booking.status !== 'completed' && booking.status !== 'cancelled') {
                const subtotal = booking.subtotal || 0;
                const discountAmount = booking.discount_amount || 0;
                return sum + (subtotal - discountAmount);
              }
              return sum;
            }, 0) || 0;

            setCompletedSales(completedTotal);
            setPendingSales(pendingTotal);
            setTotalSales(completedTotal + pendingTotal);

            // Get current month's start and end dates
            const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            // Fetch flat rate earnings
            const { data: flatRateEntries, error: flatRateError } = await supabase
              .from('payroll_page')
              .select('total_earnings')
              .eq('entry_type', 'flat_rate')
              .eq('business_id', selectedBusiness?.id)
              .gte('completed_at', currentMonthStart.toISOString())
              .lte('completed_at', currentMonthEnd.toISOString());

            if (flatRateError) {
              console.error('Error fetching flat rate earnings:', flatRateError);
            } else {
              const flatRateTotal = flatRateEntries?.reduce((sum, entry) => {
                return sum + (entry.total_earnings || 0);
              }, 0) || 0;
              setTotalFlatRateEarnings(flatRateTotal);
            }

            // Fetch estimated flat rate earnings from bookings
            console.log('Date range for query:', {
              currentMonthStart: currentMonthStart.toISOString(),
              currentMonthEnd: currentMonthEnd.toISOString(),
              selectedDateRange: selectedDateRange.id
            });

            const { data: estimatedEarnings, error: estimatedError } = await supabase
              .from('bookings')
              .select('id, status, estimated_earnings, end_date, business_id')
              .eq('business_id', selectedBusiness?.id)
              .in('status', ['scheduled'])  // Simplified to just 'scheduled' since that's what we see in the DB
              .gte('end_date', startDate.toISOString())  // Use startDate instead of currentMonthStart
              .lte('end_date', endDate.toISOString());   // Use endDate instead of currentMonthEnd

            if (estimatedError) {
              console.error('Error fetching estimated earnings:', estimatedError);
            } else {
              console.log('Selected business:', selectedBusiness);
              console.log('Estimated earnings query results:', estimatedEarnings);
              console.log('Query filters:', {
                business_id: selectedBusiness?.id,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString()
              });
              
              const estimatedTotal = estimatedEarnings?.reduce((sum, booking) => {
                console.log('Processing booking:', {
                  id: booking.id,
                  status: booking.status,
                  estimated_earnings: booking.estimated_earnings,
                  end_date: booking.end_date,
                  business_id: booking.business_id
                });
                return sum + (Number(booking.estimated_earnings) || 0);  // Added Number() conversion
              }, 0) || 0;
              console.log('Final estimated total:', estimatedTotal);
              setEstimatedFlatRateEarnings(estimatedTotal);
            }

            // Fetch hourly earnings with more details for pattern analysis
            const { data: hourlyEntries, error: hourlyError } = await supabase
              .from('payroll_page')
              .select('total_earnings, clock_out')
              .eq('entry_type', 'timeclock')
              .eq('business_id', selectedBusiness?.id)
              .gte('clock_out', new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString()) // Get last month too
              .lte('clock_out', currentMonthEnd.toISOString())
              .order('clock_out', { ascending: true });

            if (hourlyError) {
              console.error('Error fetching hourly earnings:', hourlyError);
            } else {
              // Calculate current month's hourly total
              const currentMonthEntries = hourlyEntries?.filter(entry => 
                new Date(entry.clock_out) >= currentMonthStart &&
                new Date(entry.clock_out) <= currentMonthEnd
              ) || [];

              const hourlyTotal = currentMonthEntries.reduce((sum, entry) => {
                return sum + (entry.total_earnings || 0);
              }, 0);
              setTotalHourlyEarnings(hourlyTotal);

              // Calculate estimated earnings for remaining days
              if (hourlyEntries?.length) {
                const lastEntry = currentMonthEntries[currentMonthEntries.length - 1];
                const lastEntryDate = lastEntry ? new Date(lastEntry.clock_out) : currentMonthStart;
                
                // Calculate average daily earnings
                const dailyEarnings = new Map();
                hourlyEntries.forEach(entry => {
                  const date = new Date(entry.clock_out).toISOString().split('T')[0];
                  dailyEarnings.set(date, (dailyEarnings.get(date) || 0) + entry.total_earnings);
                });

                const avgDailyEarnings = Array.from(dailyEarnings.values()).reduce((sum, val) => sum + val, 0) / dailyEarnings.size;

                // Count remaining workdays (excluding weekends)
                const remainingDays = [];
                let currentDate = new Date(lastEntryDate);
                currentDate.setDate(currentDate.getDate() + 1); // Start from next day

                while (currentDate <= currentMonthEnd) {
                  const dayOfWeek = currentDate.getDay();
                  if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude weekends
                    remainingDays.push(new Date(currentDate));
                  }
                  currentDate.setDate(currentDate.getDate() + 1);
                }

                const estimatedTotal = avgDailyEarnings * remainingDays.length;
                setEstimatedHourlyEarnings(estimatedTotal);

                console.log('Hourly earnings estimation:', {
                  lastEntryDate,
                  avgDailyEarnings,
                  remainingWorkdays: remainingDays.length,
                  estimatedTotal
                });
              }
            }

            // Fetch supplies costs from stock history for current month
            const { data: suppliesEntries, error: suppliesError } = await supabase
              .from('stock_history_with_details')
              .select('*')
              .eq('transaction_type', 'Received')
              .gte('created_at', currentMonthStart.toISOString())
              .lte('created_at', currentMonthEnd.toISOString());

            if (suppliesError) {
              console.error('Error fetching supplies:', suppliesError);
            } else {
              const suppliesCost = suppliesEntries?.reduce((sum, entry) => {
                const quantity = Math.abs(Number(entry.quantity_change) || 0);
                const price = Number(entry.price) || 0;
                return sum + (quantity * price);
              }, 0) || 0;
              setTotalSuppliesCost(suppliesCost);
            }

            // Fetch fixed expenses from operating_costs
            const { data: fixedExpenses, error: fixedExpensesError } = await supabase
              .from('operating_costs')
              .select('amount')
              .eq('business_id', selectedBusiness?.id);

            if (fixedExpensesError) {
              console.error('Error fetching fixed expenses:', fixedExpensesError);
            } else {
              const fixedExpensesTotal = fixedExpenses?.reduce((sum, expense) => {
                return sum + (Number(expense.amount) || 0);
              }, 0) || 0;
              setTotalFixedExpenses(fixedExpensesTotal);
            }
          } catch (error) {
            console.error('Error in data fetching:', error);
          } finally {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };

    fetchSalesData();
  }, [selectedDateRange, customStartDate, customEndDate]);

  const handlePageSelect = (path: string) => {
    navigate(path);
    setIsDropdownOpen(false);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteFlatRateTemplate(templateId);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Navigation Bar */}
      <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
        <div className="px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 md:py-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 md:gap-4">
            {/* Page Selector */}
            <div className="relative w-full sm:w-auto" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`
                  w-full sm:w-auto inline-flex items-center justify-between gap-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 
                  text-sm font-medium text-gray-700 
                  bg-white shadow-sm 
                  border border-gray-200 rounded-lg
                  transition-all duration-200 ease-in-out
                  hover:bg-gray-50 hover:border-gray-300
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20
                  ${isDropdownOpen ? 'bg-gray-50 border-gray-300' : ''}
                `}
              >
                <span className="text-gray-900">{getCurrentPageName()}</span>
                <ChevronDown 
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                  aria-hidden="true"
                />
              </button>

              {/* Page Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200/50 backdrop-blur-sm z-50 overflow-hidden">
                  <div className="py-1">
                    {reportPages.map((page) => {
                      const Icon = page.icon;
                      const isActive = page.path === location.pathname;
                      return (
                        <button
                          key={page.id}
                          onClick={() => handlePageSelect(page.path)}
                          className={`
                            w-full flex items-center px-4 py-2.5 text-sm
                            transition-colors duration-150 ease-in-out
                            ${isActive 
                              ? 'bg-blue-50/80 text-blue-600' 
                              : 'text-gray-700 hover:bg-gray-50'
                            }
                          `}
                        >
                          <Icon className={`h-4 w-4 mr-3 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                          {page.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Date Selector */}
            <div className="relative w-full sm:w-auto" ref={dateDropdownRef}>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                  className={`
                    flex-1 sm:flex-none inline-flex items-center justify-between gap-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 
                    text-sm font-medium text-gray-700 
                    bg-white shadow-sm 
                    border border-gray-200 rounded-lg
                    transition-all duration-200 ease-in-out
                    hover:bg-gray-50 hover:border-gray-300
                    focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    ${isDateDropdownOpen ? 'bg-gray-50 border-gray-300' : ''}
                  `}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Calendar className="h-4 w-4 flex-shrink-0 text-gray-500" />
                    <span className="text-gray-900 truncate">{selectedDateRange.name}</span>
                  </div>
                  <ChevronDown 
                    className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isDateDropdownOpen ? 'rotate-180' : ''}`} 
                    aria-hidden="true"
                  />
                </button>
                <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium truncate">
                  {getDateRangeText(selectedDateRange.id, customStartDate, customEndDate)}
                </div>
              </div>

              {/* Date Dropdown Menu */}
              {isDateDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200/50 backdrop-blur-sm z-50 overflow-hidden">
                  <div className="py-1">
                    {dateRanges.map((dateRange) => {
                      const isActive = dateRange.id === selectedDateRange.id;
                      return (
                        <button
                          key={dateRange.id}
                          onClick={() => {
                            setSelectedDateRange(dateRange);
                            setIsDateDropdownOpen(false);
                            if (dateRange.id === 'custom') {
                              setIsCustomRangeOpen(true);
                            }
                          }}
                          className={`
                            w-full flex items-center px-4 py-2.5 text-sm
                            transition-colors duration-150 ease-in-out
                            ${isActive 
                              ? 'bg-blue-50/80 text-blue-600' 
                              : 'text-gray-700 hover:bg-gray-50'
                            }
                          `}
                        >
                          {dateRange.icon && <dateRange.icon className={`h-4 w-4 mr-3 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />}
                          {dateRange.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-x-hidden">
        <div className="w-full max-w-full px-1 sm:px-3 md:px-6 py-1 sm:py-3 md:py-4 space-y-2 sm:space-y-6 overflow-x-hidden">
          {/* Estimated Profit Report */}
          <EstimatedProfitReport
            totalSales={totalSales}
            completedSales={completedSales}
            pendingSales={pendingSales}
            totalFlatRateEarnings={totalFlatRateEarnings}
            estimatedFlatRateEarnings={estimatedFlatRateEarnings}
            totalHourlyEarnings={totalHourlyEarnings}
            estimatedHourlyEarnings={estimatedHourlyEarnings}
            totalSuppliesCost={totalSuppliesCost}
            totalFixedExpenses={totalFixedExpenses}
          />
        </div>
      </div>
    </div>
  );
} 